import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { CATALOG } from "@/constants/testIds";
import RevealLines from "@/components/site/RevealLines";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] },
  },
};

export default function Catalog() {
  const [books, setBooks] = useState([]);
  const [genre, setGenre] = useState("All");

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data));
  }, []);

  const genres = ["All", ...Array.from(new Set(books.map((b) => b.genre).filter(Boolean)))];
  const filtered = genre === "All" ? books : books.filter((b) => b.genre === genre);

  return (
    <main className="pt-32 md:pt-40 pb-32 px-6 md:px-10">
      <div className="max-w-[1600px] mx-auto">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-bone-dim)] mb-6">
          — The Complete List · {books.length} Titles
        </div>
        <h1 className="font-serif-vv text-6xl md:text-[10vw] leading-[0.9] tracking-tighter">
          <RevealLines lines={["Catalog."]} />
        </h1>

        <div className="mt-14 flex flex-wrap items-center gap-4 border-b vv-line pb-4">
          {genres.map((g) => (
            <button
              key={g}
              onClick={() => setGenre(g)}
              data-testid={`filter-${g.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-xs uppercase tracking-[0.3em] px-2 py-1 transition-colors ${
                genre === g
                  ? "text-[color:var(--vv-ember)]"
                  : "text-[color:var(--vv-bone-dim)] hover:text-[color:var(--vv-bone)]"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        <div
          data-testid={CATALOG.grid}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-24"
        >
          {filtered.map((b, i) => (
            <motion.article
              key={b.id}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.08 }}
              className="group"
              data-testid={CATALOG.card(b.slug)}
            >
              <Link to={`/book/${b.slug}`} className="block">
                <div className="book-frame relative aspect-[3/4] overflow-hidden border vv-line p-8 md:p-12 bg-[color:var(--vv-surface)]">
                  <motion.img
                    src={b.cover_url}
                    alt={b.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
                  />
                  {b.coming_soon && (
                    <div className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase text-[color:var(--vv-ember)] border border-[color:var(--vv-ember)] px-2 py-1 bg-[color:var(--vv-bg)]/60 backdrop-blur">
                      Coming Soon
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-bone-dim)]">
                    {b.genre} · {b.year}
                  </div>
                  <h3 className="mt-2 font-serif-vv text-3xl leading-tight group-hover:italic transition-all duration-500">
                    {b.title}
                  </h3>
                  <div className="mt-1 font-serif-vv italic text-lg text-[color:var(--vv-bone-dim)]">
                    {b.author_name}
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </main>
  );
}
